import { useState } from 'react';

const industries = [
  {
    icon: 'ri-settings-3-line',
    title: 'Manufacturing',
    description:
      'From lean operations to supply chain restructuring, we help manufacturers reduce costs, improve throughput, and position for scalable growth.',
    highlights: ['Lean Process Optimization', 'Supply Chain Restructuring', 'Plant Operations Strategy'],
  },
  {
    icon: 'ri-truck-line',
    title: 'Distribution & Logistics',
    description:
      'We work with distributors to streamline fulfillment, renegotiate vendor contracts, and build the operational infrastructure needed to scale.',
    highlights: ['Fulfillment Optimization', 'Vendor Contract Strategy', 'Last-Mile Efficiency'],
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'Professional Services',
    description:
      'We help service firms improve utilization rates, build repeatable delivery models, and develop the leadership bench needed for sustainable growth.',
    highlights: ['Utilization & Billing Strategy', 'Delivery Model Design', 'Leadership Development'],
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Healthcare',
    description:
      'Our healthcare consultants bring deep expertise in practice management, revenue cycle optimization, and regulatory compliance for growing providers.',
    highlights: ['Revenue Cycle Optimization', 'Practice Management', 'Compliance Strategy'],
  },
  {
    icon: 'ri-cpu-line',
    title: 'Technology',
    description:
      'We guide tech companies through growth inflection points — from go-to-market refinement and pricing strategy to preparing for institutional investment.',
    highlights: ['Go-to-Market Strategy', 'Pricing & Packaging', 'Investor Readiness'],
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail & Consumer',
    description:
      'We help retail and consumer brands optimize their unit economics, expand into new channels, and build the operational foundation for profitable scale.',
    highlights: ['Unit Economics Analysis', 'Channel Expansion', 'Inventory Management'],
  },
];

export default function Industries() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <section className="py-20 px-6 bg-texas-red-light">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-texas-red text-xs font-bold tracking-widest uppercase mb-3 block">Sector Expertise</span>
          <h2 className="text-3xl md:text-4xl font-bold text-texas-navy mb-3">
            Industries We Serve
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Deep sector expertise across six industries — so you get a consultant who speaks your language and understands your market.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((industry, idx) => {
            const isActive = activeIdx === idx;
            return (
              <div
                key={industry.title}
                onClick={() => setActiveIdx(isActive ? null : idx)}
                className={`rounded-lg p-6 cursor-pointer transition-all duration-300 border ${
                  isActive
                    ? 'bg-texas-navy border-texas-navy text-white'
                    : 'bg-white border-gray-200 hover:border-texas-red/40'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-md flex-shrink-0 transition-colors duration-300 ${
                    isActive ? 'bg-texas-red text-white' : 'bg-texas-red/10 text-texas-red'
                  }`}>
                    <i className={`${industry.icon} text-xl`} />
                  </div>
                  <h3 className={`text-sm font-bold leading-snug transition-colors duration-300 ${
                    isActive ? 'text-white' : 'text-texas-navy'
                  }`}>
                    {industry.title}
                  </h3>
                </div>

                <p className={`text-sm leading-relaxed mb-4 transition-colors duration-300 ${
                  isActive ? 'text-white/75' : 'text-gray-500'
                }`}>
                  {industry.description}
                </p>

                <ul className="flex flex-col gap-1.5">
                  {industry.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-checkbox-circle-line text-sm ${isActive ? 'text-texas-red' : 'text-texas-red'}`} />
                      </div>
                      <span className={`text-xs font-medium transition-colors duration-300 ${
                        isActive ? 'text-white/75' : 'text-gray-500'
                      }`}>
                        {h}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center gap-1">
                  <span className={`text-xs font-semibold transition-colors duration-300 ${
                    isActive ? 'text-white/50' : 'text-texas-red'
                  }`}>
                    {isActive ? 'Click to collapse' : 'Learn more'}
                  </span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`text-xs transition-all duration-300 ${
                      isActive ? 'ri-arrow-up-s-line text-white/50' : 'ri-arrow-right-s-line text-texas-red'
                    }`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-400 mt-10">
          Don't see your industry?{' '}
          <a href="#contact" className="text-texas-red font-medium hover:underline cursor-pointer">
            Reach out — we may still be a great fit.
          </a>
        </p>
      </div>
    </section>
  );
}
